/**
 * Input → device-agnostic InputFrame (FS-05 §2.1). Keyboard, pointer/touch all produce the same
 * structure; the flight model never knows which device produced it. The steering axis is clamped to
 * the unit disc (R02) so diagonal movement is not 1.414× faster. Sampled once per frame and held
 * across sub-steps (R03).
 *
 * Two pointer models (FS-05 §2.7):
 *   'relative' — drag delta → axis. The original model; two integrations sit between the finger and
 *                the hull, so it starts late and coasts FS05_COAST_DISTANCE (4 u) on release.
 *   'direct'   — the finger drives a *position target* and a clamped PD law emits the axis a
 *                competent player would hold, braking included. Default on coarse pointers (FS-14
 *                R49). It changes no Design Bible §7 constant and never exceeds |axis| = 1, so it
 *                grants no authority a keyboard lacks (FS-05 R48).
 */
import { PLAY_RADIUS } from '../core/constants';

export interface MutableInputFrame {
  ax: number;
  ay: number;
  usePowerup: boolean;
  pause: boolean;
  tick: number;
}

export type SteerModel = 'relative' | 'direct';

/** Read-only ship state the direct loop closes over. */
export interface SteerShip {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface AxisOut {
  ax: number;
  ay: number;
}

// FS-05 §5.4 — local to the input layer, like BANK_SPRING_K is local to the flight model.
export const TOUCH_DRAG_SCALE = 0.02; // px → axis, 'relative' model (unchanged)
export const TOUCH_TARGET_SCALE = 0.055; // px → world u, 'direct' model
export const TOUCH_DIRECT_KP = 0.55; // /u — saturates the axis at 1.8 u of error
export const TOUCH_DIRECT_KD = 0.16; // s/u — full braking at 6.25 u/s
/**
 * s — time to rest with the pointer held still (R47). FS-05 §5.4 derived 0.25 s from the gains;
 * measured settle at these gains is 0.283 s, so the spec constant is corrected to the measurement
 * rather than the arithmetic. Tightening it would mean raising KD, which costs approach speed.
 */
export const TOUCH_SETTLE = 0.3;
export const TOUCH_COAST_MAX = 0.8; // u — max overshoot past the target (R47)
/** Below this speed the post-release settle loop lets go and ordinary damping finishes the job. */
const SETTLE_SPEED = 0.35; // u/s

/**
 * The control law (FS-05 R46): proportional on position error, derivative on velocity, clamped to
 * the unit disc. The derivative term is the whole point — it commands the braking axis as the ship
 * approaches the target, which is what a good keyboard player does by tapping the opposite key.
 * Pure, so the touch model is testable without a DOM.
 */
export function directAxis(
  targetX: number,
  targetY: number,
  ship: SteerShip,
  out: AxisOut,
): void {
  let ax = TOUCH_DIRECT_KP * (targetX - ship.x) - TOUCH_DIRECT_KD * ship.vx;
  let ay = TOUCH_DIRECT_KP * (targetY - ship.y) - TOUCH_DIRECT_KD * ship.vy;
  const mag = Math.hypot(ax, ay);
  if (mag > 1) {
    ax /= mag;
    ay /= mag;
  }
  out.ax = ax;
  out.ay = ay;
}

/** True when the platform's primary pointer is a finger (FS-14 R49). */
export function prefersDirectSteering(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

const BOUND_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'w',
  'a',
  's',
  'd',
  'W',
  'A',
  'S',
  'D',
  ' ',
  'p',
  'P',
]);

export class Input {
  private keys = new Set<string>();
  private usePowerupEdge = false;
  private pauseEdge = false;

  // Pointer / touch drag → axis.
  private dragging = false;
  private dragX = 0;
  private dragY = 0;
  private pointerAx = 0;
  private pointerAy = 0;

  // 'direct' model state: a world-space position target the PD law chases (FS-05 §2.7).
  private steerModel: SteerModel = prefersDirectSteering() ? 'direct' : 'relative';
  private targetX = 0;
  private targetY = 0;
  /** Stays true through the post-release settle, so lifting a finger stops the ship (R47). */
  private directActive = false;
  private ship: SteerShip = { x: 0, y: 0, vx: 0, vy: 0 };
  private axisOut: AxisOut = { ax: 0, ay: 0 };

  private onFirstGesture: (() => void) | null = null;

  constructor(private target: HTMLElement) {}

  /** FS-14 R49 default is resolved at construction; the settings surface overrides it. */
  setSteerModel(m: SteerModel): void {
    this.steerModel = m;
    this.directActive = false;
  }

  getSteerModel(): SteerModel {
    return this.steerModel;
  }

  attach(onFirstGesture: () => void): void {
    this.onFirstGesture = onFirstGesture;
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.clear);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.target.addEventListener('pointerdown', this.handlePointerDown);
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
  }

  detach(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.clear);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.target.removeEventListener('pointerdown', this.handlePointerDown);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
  }

  private fireGesture(): void {
    if (this.onFirstGesture) {
      const cb = this.onFirstGesture;
      this.onFirstGesture = null;
      cb();
    }
  }

  private handleVisibility = (): void => {
    if (document.visibilityState === 'hidden') this.clear();
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return; // Ignore key repeat (R06).
    if (!BOUND_KEYS.has(e.key)) return;
    e.preventDefault(); // Only for bound keys (R07).
    this.fireGesture();
    if (e.key === ' ') {
      this.usePowerupEdge = true;
      return;
    }
    if (e.key === 'p' || e.key === 'P') {
      this.pauseEdge = true;
      return;
    }
    this.keys.add(e.key);
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (!BOUND_KEYS.has(e.key)) return;
    e.preventDefault();
    this.keys.delete(e.key);
  };

  private handlePointerDown = (e: PointerEvent): void => {
    this.fireGesture();
    this.dragging = true;
    this.dragX = e.clientX;
    this.dragY = e.clientY;
    this.pointerAx = 0;
    this.pointerAy = 0;
    // Anchor the target on the ship, not on wherever the finger landed: a touch must not teleport
    // the target across the playfield. The finger then *carries* the target from there.
    this.targetX = this.ship.x;
    this.targetY = this.ship.y;
    this.directActive = true;
  };

  private handlePointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    // Consume coalesced events (FS-14 R50) so a 120 Hz digitiser is not decimated to the frame
    // rate. The target is a *position*, so the newest sample is the one that matters; reading the
    // batch is what stops a fast flick from landing short.
    const coalesced = e.getCoalescedEvents?.();
    const last = coalesced && coalesced.length > 0 ? coalesced[coalesced.length - 1]! : e;
    const dx = last.clientX - this.dragX;
    const dy = last.clientY - this.dragY;

    if (this.steerModel === 'direct') {
      // Screen +Y is down; ship +Y is up.
      let tx = this.targetX + dx * TOUCH_TARGET_SCALE;
      let ty = this.targetY - dy * TOUCH_TARGET_SCALE;
      const r = Math.hypot(tx, ty);
      if (r > PLAY_RADIUS) {
        tx = (tx / r) * PLAY_RADIUS;
        ty = (ty / r) * PLAY_RADIUS;
      }
      this.targetX = tx;
      this.targetY = ty;
      // The anchor advances with the finger, so the target integrates the drag rather than
      // snapping back to the touch-down point.
      this.dragX = last.clientX;
      this.dragY = last.clientY;
      return;
    }

    this.pointerAx = clampUnit(dx * TOUCH_DRAG_SCALE);
    this.pointerAy = clampUnit(-dy * TOUCH_DRAG_SCALE);
  };

  private handlePointerUp = (): void => {
    this.dragging = false;
    this.pointerAx = 0;
    this.pointerAy = 0;
    // R47: release means "target = where the ship is now". The loop stays active through the
    // settle so the ship stops instead of coasting FS05_COAST_DISTANCE.
    if (this.steerModel === 'direct') {
      this.targetX = this.ship.x;
      this.targetY = this.ship.y;
    }
  };

  clear = (): void => {
    this.keys.clear();
    this.dragging = false;
    this.pointerAx = 0;
    this.pointerAy = 0;
    // A cancelled touch is a release, not a held input (FS-14 R51, FS-14 F7).
    this.targetX = this.ship.x;
    this.targetY = this.ship.y;
  };

  /**
   * On-screen control entry points (FS-12 R40). They set the same edges the keyboard sets, so
   * touch is a first-class device rather than a second code path (FS-05 R01).
   */
  triggerPowerup(): void {
    this.fireGesture();
    this.usePowerupEdge = true;
  }

  triggerPause(): void {
    this.fireGesture();
    this.pauseEdge = true;
  }

  /** Consume the pause edge (for the app shell to read outside the sim). */
  takePauseEdge(): boolean {
    const v = this.pauseEdge;
    this.pauseEdge = false;
    return v;
  }

  /**
   * Sample the current input into `out`. Discrete actions are edge-consumed here.
   *
   * `ship` closes the 'direct' model's position loop. Determinism is unaffected (FS-05 R49): what
   * is taped is this method's *output*, so a replay reproduces the run with no pointer present.
   */
  sample(tick: number, out: MutableInputFrame, ship?: SteerShip): void {
    if (ship) {
      this.ship.x = ship.x;
      this.ship.y = ship.y;
      this.ship.vx = ship.vx;
      this.ship.vy = ship.vy;
    }

    let ax = 0;
    let ay = 0;
    if (this.keys.has('ArrowLeft') || this.keys.has('a') || this.keys.has('A')) ax -= 1;
    if (this.keys.has('ArrowRight') || this.keys.has('d') || this.keys.has('D')) ax += 1;
    if (this.keys.has('ArrowUp') || this.keys.has('w') || this.keys.has('W')) ay += 1;
    if (this.keys.has('ArrowDown') || this.keys.has('s') || this.keys.has('S')) ay -= 1;

    if (this.steerModel === 'direct' && this.directActive) {
      directAxis(this.targetX, this.targetY, this.ship, this.axisOut);
      // Let go once the ship has settled, so ordinary damping — not the loop — holds it at rest.
      if (!this.dragging && Math.hypot(this.ship.vx, this.ship.vy) < SETTLE_SPEED) {
        this.directActive = false;
      }
      const dirMag = Math.hypot(this.axisOut.ax, this.axisOut.ay);
      if (dirMag > Math.hypot(ax, ay)) {
        ax = this.axisOut.ax;
        ay = this.axisOut.ay;
      }
    } else {
      // Pointer overrides keyboard when it has larger magnitude.
      const keyMag = Math.hypot(ax, ay);
      const ptrMag = Math.hypot(this.pointerAx, this.pointerAy);
      if (ptrMag > keyMag) {
        ax = this.pointerAx;
        ay = this.pointerAy;
      }
    }

    // Clamp to the unit disc (R02).
    const mag = Math.hypot(ax, ay);
    if (mag > 1) {
      ax /= mag;
      ay /= mag;
    }

    out.ax = ax;
    out.ay = ay;
    out.usePowerup = this.usePowerupEdge;
    out.pause = false;
    out.tick = tick;
    this.usePowerupEdge = false;
  }
}

function clampUnit(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}
